import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './useAuth';
import { hasRole, type Role } from './roles';
import { Spinner } from '../components/ui/Spinner';

/**
 * Route gate. Order matters: session → status → MFA-enrolment funnel → role.
 * No special-case bypasses — role checks derive from the same additive model as the server.
 */
export function ProtectedRoute({ minRole, children }: { minRole?: Role; children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner center />;

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (user.status !== 'Active') {
    return <Navigate to="/pending-approval" replace />;
  }

  if (user.mustEnrolMfa && location.pathname !== '/mfa/setup') {
    return <Navigate to="/mfa/setup" replace />;
  }

  if (minRole && !hasRole(user.role, minRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
